import { useState, useEffect } from 'react';
import { Fab, Webchat } from '@botpress/webchat';
import { motion, AnimatePresence } from 'framer-motion';
import './BotpressChat.css';

export interface BotpressChatProps {
  clientId: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  position?: 'bottom-right' | 'bottom-left';
}

export const BotpressChat: React.FC<BotpressChatProps> = ({ 
  clientId, 
  isOpen = false, 
  onToggle,
  position = 'bottom-right',
}) => {
  const [isWebchatOpen, setIsWebchatOpen] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Show the chat button after a delay to avoid immediate popup
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Set Botpress CSS variables for custom styling
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--fab-bg', '#00AE9B'); // Your teal color
    root.style.setProperty('--fab-icon', '#FFFFFF'); // White icon
    root.style.setProperty('--bpRadius-scale', '1');
    root.style.setProperty('--bpRadius-full', '50%');
    
    return () => {
      root.style.removeProperty('--fab-bg');
      root.style.removeProperty('--fab-icon');
      root.style.removeProperty('--bpRadius-scale');
      root.style.removeProperty('--bpRadius-full');
    };
  }, []);

  const toggleWebchat = () => {
    const newState = !isWebchatOpen;
    setIsWebchatOpen(newState);
    onToggle?.(newState);
  };

  const isRight = position === 'bottom-right';
  const chatPosition = isRight ? { right: '20px' } : { left: '20px' };
  const fabPosition = isRight ? { right: '20px' } : { left: '20px' };

  // Responsive chat dimensions
  const chatWidth = isMobile ? '90vw' : '400px';
  const chatHeight = isMobile ? '70vh' : '600px';

  // Botpress configuration for custom branding
  const webchatConfig = {
    botName: "Headstart Intake Support",
    botAvatar: "/images/admin-chat.png",
    botDescription:
      "Ask about getting started with ABA therapy through Headstart!",
    color: "#00AE9B",
    themeMode: "light" as const,
    radius: 3,
    variant: "soft" as const,
    fontFamily: "inter" as const,
    composerPlaceholder: "Type your message...",
    showPoweredBy: false,
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{
              opacity: isWebchatOpen ? 1 : 0,
              scale: isWebchatOpen ? 1 : 0.8,
            }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              position: "fixed",
              bottom: "90px",
              ...chatPosition,
              zIndex: 1000,
            }}
          >
            <Webchat
              clientId={clientId}
              configuration={webchatConfig}
              style={{
                width: chatWidth,
                height: chatHeight,
                display: isWebchatOpen ? "flex" : "none",
                borderRadius: "12px",
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                border: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{
              position: "fixed",
              bottom: "20px",
              ...fabPosition,
              zIndex: 1001,
            }}
          >
            <Fab
              onClick={toggleWebchat}
              style={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                width: "64px",
                height: "64px",
              }}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};